// How long the OLDEST waiting save has been on this phone. Pure, so the badge
// and its detail modal agree and the bands are unit-tested.
//
// Mirrors the idea of the face kiosk's AgeingState (faceClockQueue.ts), not
// its numbers: that queue has a hard server deadline; this one has two soft ones.
//
// 4 h  = longer than a normal blip, shorter than the typical orchard
//        dead-zone stretch measured on prod.
// 20 h = before the next day's pay close, when a late pay entry is refused
//        (payroll period lock, mig 390; BigHarvest closed tickets).
export const QUEUE_AGEING_MS  = 4 * 60 * 60 * 1000
export const QUEUE_CRITICAL_MS = 20 * 60 * 60 * 1000

export type QueueAgeingState = 'ok' | 'ageing' | 'critical'

export interface QueueAgeing {
  state: QueueAgeingState
  oldestAgeMs: number | null
}

/** Failed entries are ignored: they have their own Retry path and will not
 *  send by themselves, so their age says nothing about "get signal". An
 *  unparseable queued_at is ignored too; a future one (clock skew) is age 0. */
export function queueAgeing(
  entries: ReadonlyArray<{ queued_at: string; failed?: boolean }>,
  nowMs: number,
): QueueAgeing {
  let oldest: number | null = null
  for (const e of entries) {
    if (e.failed) continue
    const t = Date.parse(e.queued_at)
    if (!Number.isFinite(t)) continue
    const age = Math.max(0, nowMs - t)
    if (oldest === null || age > oldest) oldest = age
  }
  if (oldest === null) return { state: 'ok', oldestAgeMs: null }
  if (oldest >= QUEUE_CRITICAL_MS) return { state: 'critical', oldestAgeMs: oldest }
  if (oldest >= QUEUE_AGEING_MS)   return { state: 'ageing', oldestAgeMs: oldest }
  return { state: 'ok', oldestAgeMs: oldest }
}

/** "45 min" under 1 h, "5 h" under 48 h, else "3 days" (floored). */
export function queueAgeText(ms: number): string {
  const HOUR = 60 * 60 * 1000
  if (ms < HOUR) return `${Math.floor(ms / 60_000)} min`
  if (ms < 48 * HOUR) return `${Math.floor(ms / HOUR)} h`
  return `${Math.floor(ms / (24 * HOUR))} days`
}
